import styled, { css } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderRadius, borderWidth } from "../../../design-system/tokens/borders";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { SoonBadge } from "../../atoms/SoonBadge";
import type { IconRailEdge } from "./IconRail.types";

/** Left rail 56px, right rail 52px (design ToolRail, RightRail). */
const WIDTH: Record<IconRailEdge, string> = { left: "3.5rem", right: "3.25rem" };

/** 24px wide, 1px line with 6px above and below (4px above the bottom slot). */
export const Divider = styled.span<{ $tight?: boolean }>`
  display: block;
  flex-shrink: 0;
  width: ${spacing[6]};
  height: ${borderWidth[1]};
  margin: ${({ $tight }) => ($tight ? spacing[1] : spacing[1.5])} 0;
  background: ${rawColors.stroke[200]};
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[1]};
`;

export const Rail = styled.aside<{ $edge: IconRailEdge; $horizontal?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: ${({ $edge }) => WIDTH[$edge]};
  height: 100%;
  padding: ${spacing[2]} 0;
  gap: ${spacing[1]};
  background: ${rawColors.structural.white};
  border-${({ $edge }) => ($edge === "left" ? "right" : "left")}: ${borderWidth[1]} solid ${rawColors.stroke[200]};
  color: ${rawColors.text.highEmphasis};
  ${({ $horizontal }) =>
    $horizontal &&
    css`
      flex-direction: row;
      width: 100%;
      height: auto;
      padding: ${spacing[1]} ${spacing[2]};
      border: 0;
      border-top: ${borderWidth[1]} solid ${rawColors.stroke[200]};
      overflow-x: auto;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
      > * {
        flex-shrink: 0;
      }
      /* Groups and the top/bottom slots (mode switch, launcher, avatar) are columns in the vertical rail. */
      > ${Group}, > div {
        flex-direction: row;
      }
      > ${Divider} {
        width: ${borderWidth[1]};
        height: ${spacing[6]};
        margin: 0 ${spacing[1.5]};
      }
    `}
`;

/** Pushes the bottom slot to the foot of the rail. */
export const Spacer = styled.div`
  flex: 1;
`;

/** Positions the indicator dot on the button's corner. */
export const ItemSlot = styled.span`
  position: relative;
  display: inline-flex;
`;

const DOT_COLORS = {
  success: rawColors.accent.green500,
  info: rawColors.secondary[500],
} as const;

export const Dot = styled.span<{ $tone: keyof typeof DOT_COLORS }>`
  position: absolute;
  top: ${spacing[1.5]};
  right: ${spacing[1.5]};
  box-sizing: content-box;
  width: 0.4375rem; /* 7px (design F23), no spacing token */
  height: 0.4375rem;
  border-radius: ${borderRadius.full};
  background: ${({ $tone }) => DOT_COLORS[$tone]};
  border: ${borderWidth[2]} solid ${rawColors.structural.white};
  pointer-events: none;
`;

/** Shortcut text in the tooltip, in the lighter brand purple. */
export const Shortcut = styled.kbd`
  font-family: inherit;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.brand.primaryLight};
`;

/** "Coming soon" tag drawn for the dark tooltip bubble. */
export const SoonTag = styled(SoonBadge)`
  border-color: transparent;
  background: ${rawColors.grey[700]};
  color: ${rawColors.accent.peach};
`;
